import { Button, Checkbox, Grid } from 'semantic-ui-react';
import {
  MedicamentEnumDto,
  TariffDto,
  TarmedProcedureDto,
  TarmedProcedureSearchRequest,
  TarmedProcedureUpdateRequest
} from 'ts-types/api.types';
import axios, { CancelTokenSource } from 'axios';
import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import {
  changeTarmedProcedureSelection,
  getAllTariffCodesForProcedure,
  searchTarmedProcedure,
  updateTarmedProcedures
} from 'services/procedureServices';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import { getAllTariffs } from 'services/tariffServices';
import { debounce } from 'ts-debounce';
import { NotificationSystemProps, withNotificationSystemContext } from 'context/NotificationSysemContext';
import { FormState } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { noop } from 'utils/functionUtils';
import LoaderComponent from 'components/LoaderComponent';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import SearchResultComponent from 'components/SearchResultComponent';

const TARIFF_CODES: Array<string> = [
  "001",
  "311",
  "312",
  "316",
  "317",
  "530"
];

const numberFormatter = Intl.NumberFormat('de-CH', {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2
});

interface ProcedureFieldProps extends FormState {
  values: Partial<TarmedProcedureSearchRequest>
}

interface Props extends MessageSourceProps, NotificationSystemProps {
}

interface State {
  foundTarmedProcedures: Array<TarmedProcedureDto>,
  tarmedProceduresLoaded: boolean,
  selectedTarmed: number | undefined,
  cancelTokenSource: CancelTokenSource,
  tariffs: Array<TariffDto>,
  selectedTariff?: number,
  tariffCodes: Array<MedicamentEnumDto>,
  forceUpdate: boolean,
  showUpdate: boolean,
  updatingProcedures: boolean,
  errorMessage?: string
}

class TarmedProceduresView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);
    const {getMessage} = this.props;
    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundTarmedProcedures: [],
      tarmedProceduresLoaded: false,
      selectedTarmed: undefined,
      tariffs: [],
      selectedTariff: undefined,
      tariffCodes: [],
      forceUpdate: false,
      showUpdate: true,
      updatingProcedures: false,
      cancelTokenSource
    };

    getAllTariffs(cancelTokenSource)
        .then(response => {
          const tariffs = response.filter(tariff => tariff.tarmedTariff);
          this.setState({
            tariffs: tariffs,
            selectedTariff: tariffs[0].id
          })
        }).catch(noop);

    getAllTariffCodesForProcedure(cancelTokenSource)
        .then(response => {
          this.setState((prevState) => {
            const tariffCodes = response.filter(tc => TARIFF_CODES.includes(tc.code));
            tariffCodes.unshift({code: "0", name: `${getMessage('tc.unknown')}`});
            return {
              tariffCodes: tariffCodes
            };
          });
        }).catch(noop);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  selectTarmedProcedure = (procedureId: number) => {
    this.setState({
      selectedTarmed: procedureId
    })
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  startUpdateTarmedProcedure = () => {
    const {selectionNotificationSystem, getMessage} = this.props;
    const {selectedTariff, forceUpdate, cancelTokenSource} = this.state;

    if (selectedTariff) {

      this.setState({
        updatingProcedures: true
      });

      const tarmedProcedureUpdateRequest: TarmedProcedureUpdateRequest = {
        tariffId: selectedTariff,
        forceUpdate: forceUpdate
      };

      const handleError = () => {
        this.setErrorMessage(getMessage("error.general"))
      };

      const onFinally = () => {
        this.setState({
          updatingProcedures: false
        })
      };

      updateTarmedProcedures(tarmedProcedureUpdateRequest, cancelTokenSource)
          .then(response => {
            this.setState({
              foundTarmedProcedures: response,
              selectedTarmed: response.length > 0 ? response[0].id : undefined
            });

            // @ts-ignore
            const updateSuccessNotification = selectionNotificationSystem.current;
            if (updateSuccessNotification) {
              updateSuccessNotification.addNotification({
                title: getMessage("tarmed.procedure.update.success"),
                message: getMessage("tarmed.procedure.successful.update"),
                level: 'success'
              });
            }

          })
          .catch(handleError)
          .finally(onFinally);
    }

  };

  changeSelection = (procedureId: number, selection: number, index: number) => {
    const {foundTarmedProcedures, cancelTokenSource} = this.state;
    let tarmedProcedures = foundTarmedProcedures;
    changeTarmedProcedureSelection(procedureId, selection, cancelTokenSource)
        .then(response => {
          tarmedProcedures[index] = response;
          this.setState({
            foundTarmedProcedures: tarmedProcedures
          })
        }).catch(noop)
  };

  handleProcedureChange = debounce(({values}: ProcedureFieldProps): void => {
    if (!this.mounted) {
      return;
    }
    const {cancelTokenSource} = this.state;

    if (values.tariffId != null && values.tariffCode != null) {

      this.setState({
        selectedTariff: values.tariffId
      });

      if (values.tariffCode === "001") {
        this.setState({
          showUpdate: true
        });
      } else {
        this.setState({
          showUpdate: false
        });
      }

      this.setState({
        tarmedProceduresLoaded: false,
        selectedTarmed: undefined
      });
      let searchRequest = {
        ...values,
        code: values.code ? values.code : "",
        tariffCode: !TARIFF_CODES.includes(values.tariffCode) ? "" : values.tariffCode
      };

      const onFinally = () => {
        this.setState({
          tarmedProceduresLoaded: true
        })
      };

      searchTarmedProcedure(searchRequest, cancelTokenSource)
          .then(response =>
              this.setState({
                foundTarmedProcedures: response,
                selectedTarmed: response.length > 0 ? response[0].id : undefined
              })
          )
          .catch(noop)
          .finally(onFinally);
    }
  }, 300);

  render(): React.ReactNode {
    const {tarmedProceduresLoaded, updatingProcedures} = this.state;
    const {getMessage} = this.props;
    return (
        <PraxStyledSegment>
          <div>
            {this.renderSearchProcedureSegment()}
          </div>
          <StyledViewTableRow>
            {!tarmedProceduresLoaded || updatingProcedures
                ? <LoaderComponent message={
                  !tarmedProceduresLoaded
                      ? getMessage("tarmed.procedure.loading")
                      : getMessage("tarmed.procedure.updating")
                }
                />
                : this.renderProceduresSegment()}
          </StyledViewTableRow>
        </PraxStyledSegment>
    );
  }

  renderSearchProcedureSegment = (): JSX.Element => {

    const {tariffs, tariffCodes} = this.state;

    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={{
              tariffId: tariffs.length > 0 ? tariffs[0].id : null,
              tariffCode: tariffCodes.length > 0 ? TARIFF_CODES[0] : null,
              code: ""
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;

    const {
      tariffs,
      tariffCodes,
      showUpdate,
      forceUpdate,
      updatingProcedures,
      errorMessage,
      foundTarmedProcedures
    } = this.state;

    const numResults = foundTarmedProcedures.length;
    const tariffOptions = tariffs.map(tariff => {

      return {
        key: (tariff.id),
        text: tariff.description,
        value: tariff.id
      }
    });

    let tariffCodesOptions = tariffCodes!.map((trCode, index) => {

      if (index == 0) {
        return {
          key: 0,
          text: getMessage('tc.unknown'),
          value: "0"
        }
      }
      return {
        key: (trCode.code),
        text: `${trCode.code}, ${getMessage(`tc.${trCode.name}`)}`,
        value: trCode.code
      }
    });

    return (
        <PxGrid>
          {errorMessage
          && (
              <Grid.Row>
                <Grid.Column>
                  <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                    {errorMessage}
                  </StyledErrorMessage>
                </Grid.Column>
              </Grid.Row>
          )
          }

          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("tariff")}:
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="tariffId"
                  component={Select}
                  fluid
                  options={tariffOptions}
                  disabled={updatingProcedures}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <Field
                  name="tariffCode"
                  component={Select}
                  fluid
                  options={tariffCodesOptions}
                  disabled={updatingProcedures}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("code")}:
            </Grid.Column>
            <Grid.Column width={2}>
              <Field
                  name="code"
                  fluid
                  component={Input}
                  autoFocus
                  disabled={updatingProcedures}
              />
            </Grid.Column>
            <Grid.Column width={2}></Grid.Column>
            {showUpdate &&
            <React.Fragment>
              <Grid.Column width={1}>
                <Button compact primary
                        onClick={() => this.startUpdateTarmedProcedure()}
                        disabled={updatingProcedures}
                >
                  {getMessage("button.update")}
                </Button>
              </Grid.Column>
              <Grid.Column width={4} verticalAlign="middle">
                <Checkbox
                    checked={forceUpdate}
                    onChange={(evt) => this.setState({forceUpdate: !forceUpdate})}
                    label={getMessage('procedure.force.update')}
                    value={forceUpdate.toString()}
                    disabled={updatingProcedures}
                />
              </Grid.Column>
            </React.Fragment>
            }
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleProcedureChange}/>
        </PxGrid>
    );
  };

  renderValueCell = ({cellData}: any) => {
    return numberFormatter.format(cellData);
  };

  proceduresRowGetter = ({index}: any) => {
    const {foundTarmedProcedures} = this.state;

    Object.assign(foundTarmedProcedures[index], {index: index + 1});

    return foundTarmedProcedures[index]
  };

  foundTarmedProceduresRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundTarmedProcedures, selectedTarmed} = this.state;

    const procedureId = foundTarmedProcedures[index].id;
    const booleanSelection = foundTarmedProcedures[index].selection;
    const selection = booleanSelection ? 0 : 1;

    if (!booleanSelection) {
      style.color = "rgb(198, 198, 203)"
    }

    if (selectedTarmed === procedureId) {
      style.backgroundColor = "#e5e5e5"
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onClick={() => this.selectTarmedProcedure(procedureId)}
            onDoubleClick={() => this.changeSelection(procedureId, selection, index)}
        >
          {columns}
        </div>
    );
  };

  renderProceduresSegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundTarmedProcedures} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundTarmedProcedures.length}
            rowGetter={this.proceduresRowGetter}
            rowRenderer={this.foundTarmedProceduresRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 250,
                label: (getMessage("code")),
                dataKey: "code"
              },
              {
                width: 150,
                label: (getMessage("procedure.tax.medicalValue")),
                dataKey: "medicalValue",
                cellRenderer: (this.renderValueCell)
              },
              {
                width: 150,
                label: (getMessage("procedure.tax.assistantValue")),
                dataKey: "assistantValue",
                cellRenderer: (this.renderValueCell)
              },
              {
                width: 150,
                label: (getMessage("procedure.tax.technicalValue")),
                dataKey: "technicalValue",
                cellRenderer: (this.renderValueCell)
              },
              {
                width: 1400,
                label: (getMessage("description")),
                dataKey: "description"
              }
            ]}
        />
    );
  };

}

export default withMessages(withNotificationSystemContext(TarmedProceduresView));
